import { useSyncExternalStore } from 'react'

export type Store<T extends object> = {
    get: () => T
    set: (partial: Partial<T> | ((state: T) => Partial<T>)) => void
    subscribe: (listener: () => void) => () => void
}

/**
 * Tiny external store shared by the page and the 3D scene. The scene reads it inside its
 * render loop with `store.get()`, React components subscribe with `useStore`.
 */
export const createStore = <T extends object>(initial: T): Store<T> => {
    let state = initial
    const listeners = new Set<() => void>()

    return {
        get: () => state,
        set: (partial) => {
            const next = typeof partial === 'function' ? partial(state) : partial

            state = { ...state, ...next }
            listeners.forEach(listener => listener())
        },
        subscribe: (listener) => {
            listeners.add(listener)

            return () => {
                listeners.delete(listener)
            }
        }
    }
}

export const useStore = <T extends object, U>(store: Store<T>, selector: (state: T) => U): U => {
    return useSyncExternalStore(
        store.subscribe,
        () => selector(store.get()),
        () => selector(store.get())
    )
}
